import { useCropStressesContext } from "context/CropStressesProvider";
import { FormSelectInput } from "form";
import { useFormikContext } from "formik";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { sortObjectsByProperty } from "utils/sortUtils";
import checkIfStressInFilteredStresses from "./checkIfStressInFilteredStresses";

const StressSelectInput = ({ isViewMode }) => {
  const { t } = useTranslation();

  const { handleChange, values, setFieldValue, setValues, validateForm } =
    useFormikContext();

  const { filteredStresses } = useFilteredStresses(values?.stressType);
  const filteredStressesOptions = useMemo(
    () =>
      filteredStresses
        ?.map(({ value, label }) => ({
          value,
          label,
        }))
        .sort((a, b) => sortObjectsByProperty(a, b, "label")) ?? [],
    [filteredStresses],
  );

  useEffect(() => {
    checkIfStressInFilteredStresses(
      isViewMode,
      values,
      setValues,
      filteredStressesOptions,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredStressesOptions]);

  const handleStressChange = (e) => {
    handleChange(e);
    const selectedStressUuid = e.target.value;

    if (!selectedStressUuid) {
      return;
    }

    const selectedStress = filteredStresses?.find(
      (stress) => stress.value === selectedStressUuid,
    );
    const selectedStressName = selectedStress?.label;
    setFieldValue("caused.stressName", selectedStressName).then(() =>
      validateForm(),
    );

    // TODO: - Update the stresstype input if not already set
    // This one needs first a conversation on how the bio, non bio stress type is saved in the action.
  };

  return (
    <FormSelectInput
      name="caused.stressId"
      isViewMode={isViewMode}
      placeholder={t("ActionModal.Stress")}
      onChange={handleStressChange}
      shouldHaveErrorBehaviorAfterSubmit
      options={filteredStressesOptions}
    />
  );
};

export default StressSelectInput;

const useFilteredStresses = (selectedStressTypeName) => {
  const { i18n } = useTranslation();
  const isEn = i18n.language.startsWith("en");
  const { cropStresses } = useCropStressesContext();

  const filteredStresses = useMemo(() => {
    let filteredStresses = [];
    if (selectedStressTypeName?.length) {
      filteredStresses = cropStresses?.filter((s) => {
        const stressTypeName = isEn
          ? s?.stressTypeName?.nameEn
          : s?.stressTypeName?.nameGr;
        return stressTypeName === selectedStressTypeName;
      });
    } else {
      filteredStresses = cropStresses;
    }
    return filteredStresses?.map((stress) => ({
      stressTypeName: isEn
        ? stress.stressTypeName.nameEn
        : stress.stressTypeName.nameGr,
      value: stress.uuid,
      label: isEn ? stress.name.nameEn : stress.name.nameGr,
    }));
  }, [cropStresses, isEn, selectedStressTypeName]);

  return { filteredStresses };
};
